import {
  getRequestGeneric,
  postRequestGeneric,
  putRequestGeneric,
  deleteRequestGeneric,
} from "@/services/backendApiGeneric";

export interface PhoneCall {
  id: number;
  /** null = Telefonat ohne Fall (Inhaber-Feedback 15.08.26, I.13) */
  idCase: number | null;
  idOrganization?: number | null;
  idUser: number;
  direction: "inbound" | "outbound";
  /** Stichwort der Telefonnotiz, z. B. „RR Bitte“. */
  keyword?: string | null;
  contactName: string | null;
  contactPhone: string | null;
  /** Gesprächspartner erreicht; null = nicht erfasst (Altbestand, Portal). */
  contactReached?: boolean | null;
  /** Gesprächsverhalten des Gesprächspartners (Schlüssel, s. utils/contactBehavior). */
  contactBehavior?: string[];
  /** Ergänzung zum Gesprächsverhalten. */
  contactBehaviorNote?: string | null;
  calledAt: string;
  durationSeconds: number | null;
  summary: string | null;
  forwardedToUser: number | null;
  forwardedNote: string | null;
  forwardedAt: string | null;
  createdAt: string;
  updatedAt: string;
  source: "manual" | "portal";
  status?: string | null;
  recordingUrl?: string | null;
  creatorName?: string;
  forwardedUserName?: string | null;
  // Optional AI transcription / summary (portal source only, Issue #207)
  aiEnabled?: boolean;
  aiStatus?: string | null;
  aiTranscript?: string | null;
  aiSummary?: string | null;
  aiError?: string | null;
  aiCompletedAt?: string | null;
  /** Nur in der Antwort auf das Anlegen: Ergebnis der E-Mail-Mitteilung. */
  notification?: NotifyResult | null;
}

export interface CreatePhoneCallPayload {
  direction: "inbound" | "outbound";
  keyword: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactReached: boolean | null;
  contactBehavior: string[];
  contactBehaviorNote: string | null;
  calledAt: string;
  durationSeconds: number | null;
  summary: string | null;
  /** Nur beim Update eines fallfreien Telefonats: nachträgliche Fallzuordnung. */
  idCase?: number | null;
  /** Nur beim Anlegen: E-Mail-Mitteilung an Sachbearbeiter / Kollegen. */
  notify?: NotifyPayload;
}

/** Wählbarer Empfänger der Telefonnotiz (Kollege der eigenen Organisation). */
export interface NotifyRecipient {
  idUser: number;
  name: string;
  email?: string;
  /** Sachbearbeiter der Akte – im Dialog vorausgewählt. */
  isSachbearbeiter: boolean;
}

export interface NotifyPayload {
  userIds: number[];
  /** Externe E-Mail-Adressen (Rückmeldung Kanzlei 01.10.2026). */
  emails: string[];
  /** Legt je Empfänger eine Wiedervorlage „Rückruf“ an (nur mit Fall). */
  callbackFollowup: boolean;
}

export interface NotifiedUser {
  idUser: number;
  name: string;
  reason?: string;
}

export interface NotifyResult {
  sent: NotifiedUser[];
  failed: NotifiedUser[];
  followupsCreated: number;
}

/**
 * Nachträgliche Mitteilung zu einem Telefonat: vermerkte Telefonate über
 * /phone-calls, Anrufe über das KARS-Telefon über /call-logs.
 */
export function notifyPhoneCall(call: Pick<PhoneCall, "id" | "source">, payload: NotifyPayload): Promise<NotifyResult> {
  const base = call.source === "portal" ? "/protected/call-logs" : "/protected/phone-calls";
  return postRequestGeneric(`${base}/${call.id}/notify`, payload, true);
}

/** Gesprächsverhalten zur Gesprächsnotiz eines Anrufs über das KARS-Telefon. */
export interface PortalCallBehavior {
  contactBehavior: string[];
  contactBehaviorNote: string | null;
}

/** Gesprächsnotiz (samt Gesprächsverhalten) zu einem Anruf über das KARS-Telefon speichern. */
export function savePortalCallNote(
  id: number,
  note: string,
  behavior: PortalCallBehavior = { contactBehavior: [], contactBehaviorNote: null },
): Promise<{ ok: boolean }> {
  return putRequestGeneric(`/protected/call-logs/${id}/note`, { note, ...behavior }, true);
}

/** Empfänger der Telefonnotiz; ohne Fall (null) sind alle Kollegen wählbar. */
export function listNotifyRecipients(idCase: number | null): Promise<NotifyRecipient[]> {
  return getRequestGeneric(
    idCase ? `/protected/cases/${idCase}/phone-calls/notify-recipients` : `/protected/phone-calls/notify-recipients`,
  );
}

export function listPhoneCalls(idCase: number): Promise<PhoneCall[]> {
  return getRequestGeneric(`/protected/cases/${idCase}/phone-calls`);
}

export function createPhoneCall(idCase: number, payload: CreatePhoneCallPayload): Promise<PhoneCall> {
  return postRequestGeneric(`/protected/cases/${idCase}/phone-calls`, payload, true);
}

/** Telefonat ohne Fall vermerken (globale Kommunikationsseite). */
export function createCaselessPhoneCall(payload: CreatePhoneCallPayload): Promise<PhoneCall> {
  return postRequestGeneric(`/protected/phone-calls`, payload, true);
}

/**
 * Ordnet ein fallfreies Telefonat nachträglich einem Fall zu. Das Backend
 * verlangt beim PUT die vollständigen Felder, daher wird der bestehende
 * Eintrag unverändert mitgeschickt.
 */
export function assignPhoneCallToCase(call: PhoneCall, idCase: number): Promise<PhoneCall> {
  return putRequestGeneric(`/protected/phone-calls/${call.id}`, {
    direction: call.direction,
    keyword: call.keyword ?? null,
    contactName: call.contactName,
    contactPhone: call.contactPhone,
    contactReached: call.contactReached ?? null,
    // Vollständiger PUT – das Gesprächsverhalten darf beim Zuordnen nicht verloren gehen.
    contactBehavior: call.contactBehavior ?? [],
    contactBehaviorNote: call.contactBehaviorNote ?? null,
    calledAt: call.calledAt,
    durationSeconds: call.durationSeconds,
    summary: call.summary,
    idCase,
  }, true);
}

export function updatePhoneCall(id: number, payload: CreatePhoneCallPayload): Promise<PhoneCall> {
  return putRequestGeneric(`/protected/phone-calls/${id}`, payload, true);
}

export function deletePhoneCall(id: number): Promise<void> {
  return deleteRequestGeneric(`/protected/phone-calls/${id}`);
}
